import { useCallback, useEffect, useState } from 'react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

/**
 * Proactively detect that an open editor tab has fallen behind the server,
 * before the next autosave conflicts. When the tab regains focus (the common
 * way a tab goes stale: you left it open while editing elsewhere), we fetch the
 * draft's current revision and compare it to the revision this tab is editing
 * from. If the server is ahead, `isStale` flips true so the editor can offer a
 * "load latest" action instead of letting the user edit into a save conflict.
 *
 * Detection is on focus/visibility only: there is no background poll, so an idle
 * tab costs nothing. `getLocalRevision` reads the editor's live revision ref, so
 * a save that advances the revision while we are checking is accounted for.
 */
export function useStaleEditorCheck({
  resourceVersionId,
  getLocalRevision,
}: {
  resourceVersionId: number | undefined;
  getLocalRevision: () => number;
}) {
  const utils = trpcAdmin.useUtils();
  const [isStale, setIsStale] = useState(false);

  const checkRevision = useCallback(async () => {
    if (resourceVersionId === undefined || isStale) {
      return;
    }

    const { revision } = await utils.resourceVersions.currentRevision.fetch({ resourceVersionId });

    if (revision !== null && revision > getLocalRevision()) {
      setIsStale(true);
    }
  }, [resourceVersionId, isStale, utils, getLocalRevision]);

  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') {
        void checkRevision();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [checkRevision]);

  return { isStale };
}
